import { useEffect, useRef, useState, useCallback } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { ScanLine, Keyboard, Camera, X, CheckCircle2 } from 'lucide-react';

/**
 * Modal per scansionare un codice a barre / QR.
 * Supporta 3 modalità:
 *  1. Lettore USB (agisce come tastiera) — attiva keyboard buffer che intercetta rapide sequenze
 *  2. Input manuale (con tastiera on-screen su mobile)
 *  3. Fotocamera (via html5-qrcode) — pulsante per aprire scan visivo
 *
 * Props:
 *  - open: bool
 *  - onOpenChange(open)
 *  - onScan(codice): callback quando viene rilevato un codice
 *  - title: default "Scansiona codice"
 */
export default function BarcodeScannerModal({ open, onOpenChange, onScan, title = 'Scansiona codice' }) {
  const [manualCode, setManualCode] = useState('');
  const [cameraActive, setCameraActive] = useState(false);
  const [cameraError, setCameraError] = useState('');
  const [detected, setDetected] = useState('');
  const bufferRef = useRef('');
  const bufferTimerRef = useRef(null);
  const scannerRef = useRef(null);
  const scannerElId = 'barcode-scanner-viewport';

  const stopCamera = useCallback(async () => {
    if (scannerRef.current) {
      try {
        await scannerRef.current.stop();
        await scannerRef.current.clear();
      } catch { /* ignore */ }
      scannerRef.current = null;
    }
    setCameraActive(false);
  }, []);

  const handleDetected = useCallback((code) => {
    const clean = String(code || '').trim();
    if (!clean) return;
    setDetected(clean);
    stopCamera();
    // Piccolo delay UX per far vedere il "detected"
    setTimeout(() => {
      onScan?.(clean);
      onOpenChange?.(false);
    }, 400);
  }, [onScan, onOpenChange, stopCamera]);

  // Reset stato all'apertura
  useEffect(() => {
    if (open) {
      setManualCode('');
      setDetected('');
      setCameraError('');
      bufferRef.current = '';
    } else {
      // Cleanup camera on close
      stopCamera();
    }
  }, [open, stopCamera]);

  // Listener globale tastiera per lettori USB (che emettono codice + Enter velocemente)
  useEffect(() => {
    if (!open) return;
    const handleKey = (e) => {
      // Se il focus è su un input del dialog, lasciamo digitare normalmente
      const active = document.activeElement;
      const isInsideInput = active && (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA');

      if (e.key === 'Enter') {
        if (bufferRef.current.length >= 3 && !isInsideInput) {
          handleDetected(bufferRef.current);
          bufferRef.current = '';
          e.preventDefault();
        }
        return;
      }
      // Solo caratteri stampabili
      if (e.key.length === 1) {
        // Se un input è a fuoco, lascia perdere il buffer
        if (isInsideInput) return;
        bufferRef.current += e.key;
        clearTimeout(bufferTimerRef.current);
        bufferTimerRef.current = setTimeout(() => { bufferRef.current = ''; }, 200);
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('keydown', handleKey);
      clearTimeout(bufferTimerRef.current);
    };
  }, [open, handleDetected]);

  const startCamera = async () => {
    setCameraError('');
    try {
      const cameras = await Html5Qrcode.getCameras();
      if (!cameras || cameras.length === 0) {
        setCameraError('Nessuna fotocamera disponibile su questo dispositivo.');
        return;
      }
      // Preferisce fotocamera posteriore
      const backCam = cameras.find((c) => /back|rear|environment/i.test(c.label)) || cameras[cameras.length - 1];
      const scanner = new Html5Qrcode(scannerElId, { verbose: false });
      scannerRef.current = scanner;
      setCameraActive(true);
      await scanner.start(
        backCam.id,
        { fps: 10, qrbox: { width: 260, height: 140 } },
        (decodedText) => {
          handleDetected(decodedText);
        },
        () => { /* ignora frame non validi */ }
      );
    } catch (err) {
      console.error('Camera error:', err);
      setCameraError(err?.message || 'Impossibile avviare la fotocamera. Assicurati di aver dato il permesso.');
      setCameraActive(false);
    }
  };

  const handleManualSubmit = (e) => {
    e?.preventDefault?.();
    if (manualCode.trim()) handleDetected(manualCode);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="barcode-scanner-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ScanLine className="h-5 w-5 text-cyan-500" />
            {title}
          </DialogTitle>
          <DialogDescription>
            Usa il lettore USB (basta scansionare), digita manualmente, o attiva la fotocamera del dispositivo.
          </DialogDescription>
        </DialogHeader>

        {detected ? (
          <div className="py-8 flex flex-col items-center gap-3">
            <CheckCircle2 className="h-16 w-16 text-emerald-500" />
            <div className="text-sm text-muted-foreground">Codice rilevato</div>
            <div className="font-mono text-xl font-bold" data-testid="barcode-detected">{detected}</div>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Lettore USB info */}
            <div className="rounded-md bg-cyan-50 dark:bg-cyan-950/20 border border-cyan-200 dark:border-cyan-900 p-3 text-sm flex items-start gap-2">
              <Keyboard className="h-5 w-5 text-cyan-600 shrink-0 mt-0.5" />
              <div>
                <div className="font-semibold text-cyan-800 dark:text-cyan-300">Lettore USB attivo</div>
                <div className="text-xs text-cyan-700 dark:text-cyan-400">Scansiona ora con il tuo lettore — il codice verrà rilevato automaticamente.</div>
              </div>
            </div>

            {/* Manuale */}
            <form onSubmit={handleManualSubmit} className="space-y-2">
              <Label htmlFor="manual-code" className="text-xs">Inserisci codice manualmente</Label>
              <div className="flex gap-2">
                <Input
                  id="manual-code"
                  data-testid="barcode-manual-input"
                  value={manualCode}
                  onChange={(e) => setManualCode(e.target.value)}
                  placeholder="Es. 8001234567890 o EP2026001"
                  autoFocus
                />
                <Button type="submit" data-testid="barcode-manual-submit" disabled={!manualCode.trim()}>
                  OK
                </Button>
              </div>
            </form>

            {/* Fotocamera */}
            <div className="pt-2 border-t">
              {!cameraActive ? (
                <Button
                  onClick={startCamera}
                  variant="outline"
                  className="w-full"
                  data-testid="barcode-camera-start"
                >
                  <Camera className="h-4 w-4 mr-2" />
                  Usa fotocamera del dispositivo
                </Button>
              ) : (
                <div className="space-y-2">
                  <div id={scannerElId} className="w-full rounded-md overflow-hidden bg-black" style={{ minHeight: 200 }} />
                  <Button
                    onClick={stopCamera}
                    variant="outline"
                    className="w-full"
                    data-testid="barcode-camera-stop"
                  >
                    <X className="h-4 w-4 mr-2" />
                    Ferma fotocamera
                  </Button>
                </div>
              )}
              {cameraError && (
                <div className="mt-2 text-xs text-rose-600" data-testid="barcode-camera-error">{cameraError}</div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
